import { createFileRoute, Link, useNavigate } from '@tanstack/react-router';
import { Mail } from 'lucide-react';
import { useEffect, useState } from 'react';

import {
  requestFailedBody,
  requestRefusal,
  useRequestVerificationEmail,
  type RequestRefusal,
} from '../api/verification-emails.js';
import { readPendingSignup } from '../auth/pending-signup.js';
import { PreAccountGate } from '../auth/PreAccountGate.js';
import { waitBody } from '../auth/signup-errors.js';
import { Button } from '../components/Button.js';
import { DangerAlert } from '../components/DangerAlert.js';
import { PageHeading } from '../components/PageHeading.js';
import { StatusCaption } from '../components/StatusCaption.js';
import text from '../components/pre-account-text.module.css';
import { PreAccountCard, PreAccountFootnote, PreAccountFrame } from '../frame/PreAccountFrame.js';

/**
 * Check your email (account sign-up `ux.md`, flow B and States: Check your
 * email). One page for every sign-up the server accepted: the dashboard can't
 * tell a new account from an address that already had a login, so every
 * sentence here is true in both cases and none says "Groundly sent" without
 * its condition.
 */
export const Route = createFileRoute('/signup/check-email')({
  component: CheckEmailPage,
});

function CheckEmailPage() {
  return (
    <PreAccountGate>
      <CheckEmail />
    </PreAccountGate>
  );
}

const timeFormat = new Intl.DateTimeFormat(undefined, { hour: '2-digit', minute: '2-digit' });

function CheckEmail() {
  const navigate = useNavigate();
  // Read once: the address this tab signed up with, kept across a reload
  // (`ux.md`, flow B6).
  const [pending] = useState(() => readPendingSignup());
  const request = useRequestVerificationEmail();
  const [requestedAt, setRequestedAt] = useState<Date | null>(null);
  const [refusal, setRefusal] = useState<RequestRefusal | null>(null);

  useEffect(() => {
    // Opened with no address, e.g. in a new tab: there is nothing to show or
    // send again, so the page that asks for the address opens instead.
    if (pending === null) void navigate({ to: '/verify-email/resend', replace: true });
  }, [navigate, pending]);

  if (pending === null) return null;

  const resend = () => {
    if (request.isPending) return;
    setRefusal(null);
    request.mutate(pending.email, {
      onSuccess: () => setRequestedAt(new Date()),
      onError: (error) => {
        setRequestedAt(null);
        setRefusal(requestRefusal(error));
      },
    });
  };

  const alert = refusal === null ? null : alertFor(refusal);

  return (
    <PreAccountFrame>
      <PreAccountCard head={<PageHeading>Check your email</PageHeading>}>
        {alert === null ? null : <DangerAlert title={alert.title}>{alert.body}</DangerAlert>}

        <p className={text.message}>
          If <span className={text.address}>{pending.email}</span> doesn&apos;t already have a
          login, Groundly sent a verification email to it. Open the link in it to verify your email
          address and continue to the dashboard.
        </p>
        <p className={text.muted}>
          If it already has a login, or you&apos;ve already verified it, nothing was changed and no
          email was sent.{' '}
          <Link to="/login" state={{ email: pending.email }}>
            Log in
          </Link>{' '}
          instead.
        </p>
        <p className={text.muted}>
          Can&apos;t find the email? Check your spam or junk folder, or request it again.
        </p>

        {/* Marked busy rather than disabled, so focus stays on it (`ux.md`, Focus). */}
        <Button
          variant="secondary"
          block
          aria-disabled={request.isPending || undefined}
          onClick={resend}
        >
          <Mail aria-hidden="true" strokeWidth={1.75} width={16} height={16} />
          {request.isPending ? 'Sending' : 'Resend verification email'}
        </Button>
        <StatusCaption
          caption={
            requestedAt === null ? null : `Requested again at ${timeFormat.format(requestedAt)}.`
          }
          announcement="Verification email requested again"
        />
      </PreAccountCard>

      <PreAccountFootnote>
        Wrong email address?{' '}
        <Link to="/signup" state={{ name: pending.name, email: pending.email }}>
          Sign up again
        </Link>
      </PreAccountFootnote>
    </PreAccountFrame>
  );
}

function alertFor(refusal: RequestRefusal): { title: string; body: string } {
  switch (refusal.kind) {
    case 'too-many':
      return { title: 'Too many verification emails requested', body: waitBody(refusal.minutes) };
    case 'no-response':
    case 'server-error':
      return {
        title: "Couldn't request the verification email",
        body: requestFailedBody(refusal.kind),
      };
    case 'field':
      // The address was accepted at sign-up, so the same rule can't refuse it
      // here; if the two ever drift apart, the server's words are shown.
      return { title: "Couldn't request the verification email", body: refusal.message };
  }
}
